import { db } from './helpers/firebase';
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  writeBatch,
  query,
  orderBy
} from 'firebase/firestore';

// Migration functions
const migrateChaptersToSubcollection = async () => {
  console.log('🚀 Starting migration of chapters to subcollections...');

  let totalMigrated = 0;
  let totalSkipped = 0;

  try {
    const languages = ['en', 'es'];
    const sectionsToMigrate = ['lectures', 'books'];

    for (const language of languages) {
      console.log(`\n🌍 Processing language: ${language}`);

      for (const section of sectionsToMigrate) {
        console.log(`\n📚 Processing section: ${section}`);

        const sectionCollectionRef = collection(db, 'contentSection', language, section);
        const snapshot = await getDocs(sectionCollectionRef);

        if (snapshot.empty) {
          console.log(`   ℹ️  No documents found in ${language}/${section}`);
          continue;
        }

        console.log(`   📄 Found ${snapshot.size} documents in ${language}/${section}`);

        for (const docSnapshot of snapshot.docs) {
          const docData = docSnapshot.data();
          const docId = docSnapshot.id;

          console.log(`\n   🔍 Processing content: ${docData.title || docId}`);

          if (!docData.chapters || !Array.isArray(docData.chapters) || docData.chapters.length === 0) {
            console.log(`     ⏭️  No chapters to migrate`);
            totalSkipped++;
            continue;
          }

          const chaptersToMigrate = docData.chapters;
          console.log(`     📖 Found ${chaptersToMigrate.length} chapters to migrate`);

          const chaptersCollectionRef = collection(db, 'contentSection', language, section, docId, 'chapters');
          const existingChaptersSnapshot = await getDocs(chaptersCollectionRef);

          if (!existingChaptersSnapshot.empty) {
            console.log(`     ✅ Chapters already migrated (${existingChaptersSnapshot.size} in subcollection)`);

            if (docData.chapters && docData.chapters.length > 0) {
              await updateDoc(doc(db, 'contentSection', language, section, docId), {
                chapters: [],
                chaptersMigratedAt: new Date().toISOString(),
                migrationNote: 'Chapters moved to subcollection'
              });
              console.log(`     🧹 Cleaned up chapters array from main document`);
            }
            totalSkipped++;
            continue;
          }

          try {
            const batch = writeBatch(db);

            for (const chapter of chaptersToMigrate) {
              const chapterDocRef = doc(
                db,
                'contentSection',
                language,
                section,
                docId,
                'chapters',
                chapter.id.toString()
              );

              const chapterData = {
                ...chapter,
                migratedAt: new Date().toISOString(),
                migratedFrom: 'document_array'
              };

              batch.set(chapterDocRef, chapterData);
              console.log(`       📝 Queued chapter ${chapter.id}: ${chapter.title || `Chapter ${chapter.id}`}`);
            }

            const mainDocRef = doc(db, 'contentSection', language, section, docId);
            batch.update(mainDocRef, {
              chapters: [],
              chaptersMigratedAt: new Date().toISOString(),
              migrationNote: `${chaptersToMigrate.length} chapters moved to subcollection`
            });

            await batch.commit();
            console.log(`     ✅ Successfully migrated ${chaptersToMigrate.length} chapters`);
            totalMigrated++;

          } catch (migrationError) {
            console.error(`     ❌ Failed to migrate chapters for ${docId}:`, migrationError);
          }
        }
      }
    }

    console.log('\n📊 Migration Summary:');
    console.log(`   • Content items migrated: ${totalMigrated}`);
    console.log(`   • Content items skipped: ${totalSkipped}`);
    console.log(`   • Total processed: ${totalMigrated + totalSkipped}`);

    if (totalMigrated > 0) {
      console.log('\n✅ Migration completed successfully!');
      console.log('\n⚠️  Next steps:');
      console.log('   1. Verify the migrated chapters in Firestore console');
      console.log('   2. Test chapter display and functionality in your app');
      console.log('   3. Monitor for any issues with the new structure');
    } else {
      console.log('\nℹ️  No chapters needed migration.');
    }

  } catch (error) {
    console.error('❌ Migration failed:', error);
    throw error;
  }
};

const checkChaptersStatus = async () => {
  console.log('🔍 Checking chapters status...');

  const languages = ['en', 'es'];
  const sectionsToCheck = ['lectures', 'books'];
  let totalWithChaptersArray = 0;
  let totalWithSubcollection = 0;

  for (const language of languages) {
    console.log(`\n🌍 Language: ${language}`);

    for (const section of sectionsToCheck) {
      console.log(`\n📚 Section: ${section}`);

      const sectionCollectionRef = collection(db, 'contentSection', language, section);
      const snapshot = await getDocs(sectionCollectionRef);

      if (snapshot.empty) {
        console.log(`   ℹ️  No documents found`);
        continue;
      }

      for (const docSnapshot of snapshot.docs) {
        const docData = docSnapshot.data();
        const docId = docSnapshot.id;

        const hasChaptersArray = docData.chapters && Array.isArray(docData.chapters) && docData.chapters.length > 0;

        const chaptersCollectionRef = collection(db, 'contentSection', language, section, docId, 'chapters');
        const existingChaptersSnapshot = await getDocs(chaptersCollectionRef);
        const hasSubcollection = !existingChaptersSnapshot.empty;

        console.log(`   📖 ${docData.title || docId}:`);
        console.log(`      Array: ${hasChaptersArray ? `✓ (${docData.chapters.length})` : '✗'}`);
        console.log(`      Subcollection: ${hasSubcollection ? `✓ (${existingChaptersSnapshot.size})` : '✗'}`);

        if (hasChaptersArray) totalWithChaptersArray++;
        if (hasSubcollection) totalWithSubcollection++;

        if (hasChaptersArray && hasSubcollection) {
          console.log(`      ⚠️  Both formats exist - needs cleanup`);
        }
      }
    }
  }

  console.log(`\n📊 Summary:`);
  console.log(`   • Items with chapters array: ${totalWithChaptersArray}`);
  console.log(`   • Items with chapters subcollection: ${totalWithSubcollection}`);
};

// Run the functions when this file is imported
// You can uncomment the one you want to run:

// To check status:
window.checkChaptersStatus = checkChaptersStatus;

// To run migration:
window.migrateChaptersToSubcollection = migrateChaptersToSubcollection;

// Make them available globally
console.log('📖 Migration functions loaded!');
console.log('Run in console:');
console.log('• checkChaptersStatus() - to check current status');
console.log('• migrateChaptersToSubcollection() - to run migration');

export { migrateChaptersToSubcollection, checkChaptersStatus };